import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./FundsDepositPage.css";
import "./FundsWithdrawalPage.css";

const DEFAULT_PAGE_LIMIT = 20;
const EXPORT_BATCH = 500;
const PAGE_LIMIT_OPTIONS = [20, 50, 100, 500, 1000];
const EXCEL_SHEET_TITLE = "Deposit History";

const truncateHash = (str, head = 8, tail = 6) => {
  if (!str || str.length <= head + tail) return str || "—";
  return `${str.slice(0, head)}...${str.slice(-tail)}`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const formatNum = (n) =>
  n != null && !Number.isNaN(Number(n))
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 12 })
    : "—";

const statusClass = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed" || s === "complete" || s === "success") return "success";
  if (s === "pending" || s === "confirming") return "pending";
  if (s === "failed" || s === "rejected" || s === "cancelled") return "failed";
  return "pending";
};

const userFullName = (user) => {
  if (!user || typeof user !== "object") return "—";
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const rowToExport = (row) => {
  const user = row.userId;
  return {
    "Created At": row.createdAt ?? "",
    "User Name": userFullName(user) === "—" ? "" : userFullName(user),
    UUID: user?.uuid ?? "",
    Email: user?.emailId ?? "",
    Mobile: user?.mobileNumber ?? "",
    "User ID": user?._id ?? "",
    Asset: row.assetId ?? "",
    "Token ID": row.tokenId ?? "",
    Chain: row.chainId ?? "",
    Amount: row.amount ?? "",
    "From Address": row.fromAddress ?? "",
    "To Address": row.toAddress ?? "",
    "Tx Hash": row.txHash ?? "",
    Confirmations: row.confirmations ?? "",
    "Confirmation Threshold": row.confirmationThreshold ?? "",
    Status: row.status ?? "",
    Credited: row.credited ? "Yes" : "No",
    "Credited At": row.creditedAt ?? "",
    "Transaction ID": row.transactionId ?? "",
    "Record ID": row._id ?? "",
  };
};

const CopyHashCell = ({ value, head = 8, tail = 6, onCopy }) => {
  if (!value) return <span className="deposits-hash">—</span>;
  return (
    <div className="withdrawal-address-cell">
      <div className="withdrawal-tooltip-wrap">
        <span className="deposits-hash withdrawal-tooltip-wrap__trigger">
          {truncateHash(value, head, tail)}
        </span>
        <div className="withdrawal-tooltip-box" role="tooltip">
          {value}
        </div>
      </div>
      <button
        type="button"
        className="withdrawal-copy-btn"
        onClick={() => onCopy(value)}
        title="Copy"
        aria-label="Copy"
      >
        <i className="fas fa-copy" />
      </button>
    </div>
  );
};

const FundsDepositPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(DEFAULT_PAGE_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [viewDetail, setViewDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState("");
  const [assetId, setAssetId] = useState("");
  const [chainId, setChainId] = useState("");
  const [txHash, setTxHash] = useState("");

  useEffect(() => {
    setPage(1);
  }, [userId, status, assetId, chainId, txHash, pageLimit]);

  const buildParams = useCallback(
    (pageNum = page, limitNum = pageLimit) => {
      const params = { page: pageNum, limit: limitNum };
      if (userId.trim()) params.userId = userId.trim();
      if (status.trim()) params.status = status.trim();
      if (assetId.trim()) params.assetId = assetId.trim();
      if (chainId.trim()) params.chainId = chainId.trim();
      if (txHash.trim()) params.txHash = txHash.trim();
      return params;
    },
    [page, pageLimit, userId, status, assetId, chainId, txHash]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getCoboDepositHistory(buildParams());
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setTotalPages(res?.pagination?.totalPages || res?.pagination?.total_pages || 1);
      setTotalCount(res?.pagination?.total ?? list.length);
      if (res?.success === false && !list.length) {
        setError(res?.message || "Failed to load deposit history.");
      }
      window.dispatchEvent(new Event("dashboardCountsUpdated"));
    } catch {
      setRows([]);
      setError("Failed to load deposit history.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setToast({ type: "success", message: "Copied to clipboard." });
      setTimeout(() => setToast(null), 2000);
    } catch {
      setToast({ type: "error", message: "Failed to copy." });
      setTimeout(() => setToast(null), 2000);
    }
  };

  const fetchAllFilteredDeposits = async () => {
    let currentPage = 1;
    let lastPage = 1;
    const all = [];

    do {
      const res = await AdminService.getCoboDepositHistory(
        buildParams(currentPage, EXPORT_BATCH)
      );
      if (res?.success === false && !Array.isArray(res?.data)) {
        throw new Error(res?.message || "Failed to fetch data for export.");
      }
      const list = Array.isArray(res?.data) ? res.data : [];
      all.push(...list);
      lastPage = res?.pagination?.totalPages || res?.pagination?.total_pages || 1;
      currentPage += 1;
    } while (currentPage <= lastPage);

    return all;
  };

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const allRows = await fetchAllFilteredDeposits();
      if (!allRows.length) {
        setToast({ type: "error", message: "No deposits match your filters to export." });
        setTimeout(() => setToast(null), 3000);
        return;
      }

      const exportData = allRows.map(rowToExport);
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, EXCEL_SHEET_TITLE);

      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `deposit_history_${dateStamp}.xlsx`);

      setToast({
        type: "success",
        message: `Exported ${allRows.length} deposit(s) to Excel.`,
      });
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      console.error(e);
      setToast({
        type: "error",
        message: e?.message || "Failed to export Excel file.",
      });
      setTimeout(() => setToast(null), 4000);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageLimitChange = (e) => {
    setPageLimit(Number(e.target.value) || DEFAULT_PAGE_LIMIT);
    setPage(1);
  };

  const pageStart = rows.length === 0 ? 0 : (page - 1) * pageLimit + 1;
  const pageEnd = rows.length === 0 ? 0 : (page - 1) * pageLimit + rows.length;

  const tableColSpan = useMemo(() => 15, []);

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Deposit</span>
          </p>
          <h1 className="dashboard-hero__title">Deposit</h1>
          <p className="dashboard-hero__subtitle">
            Manage deposit requests and Cobo deposit history
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-arrow-down" />
        </div>
      </section>

      {toast && (
        <div
          className={`withdrawal-toast withdrawal-toast--${toast.type}`}
          role="status"
        >
          <i className={`fas fa-${toast.type === "success" ? "check-circle" : "exclamation-circle"}`} />
          {toast.message}
        </div>
      )}

      <div className="dashboard-body">
        <section className="deposits-section">
          <div className="deposits-section__header">
            <div className="deposits-section__title-wrap">
              <span className="deposits-section__icon">
                <i className="fas fa-wallet" />
              </span>
              <h2 className="deposits-section__title">Deposit History</h2>
            </div>
            <div className="deposits-section__stats deposits-section__stats--actions">
              <span className="deposits-stat">
                <strong>{rows.length}</strong> on page
                {totalCount != null && (
                  <>
                    {" "}
                    (<strong>{totalCount}</strong> total)
                  </>
                )}
              </span>
              {totalPages > 1 && (
                <span className="deposits-stat">
                  Page <strong>{page}</strong> / <strong>{totalPages}</strong>
                </span>
              )}
              <button
                type="button"
                className="deposits-export-btn"
                onClick={handleExportExcel}
                disabled={exportLoading || loading}
                title="Download filtered deposits as Excel"
              >
                <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
                {exportLoading ? "Exporting…" : "Download Excel"}
              </button>
              <button
                type="button"
                className="withdrawal-refresh-btn"
                onClick={fetchRows}
                disabled={loading}
                title="Refresh"
              >
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div
            className="deposits-search-wrap"
            style={{ flexWrap: "wrap", gap: "0.5rem" }}
          >
            <input
              type="text"
              placeholder="User ID (Mongo _id)"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="deposits-search-input"
              style={{ maxWidth: "none", flex: "1 1 180px" }}
            />
            <input
              type="text"
              placeholder="Status (e.g. Completed)"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="deposits-search-input"
              style={{ maxWidth: "none", flex: "1 1 140px" }}
            />
            <input
              type="text"
              placeholder="Asset (USDT, BNB…)"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="deposits-search-input"
              style={{ maxWidth: "none", flex: "1 1 120px" }}
            />
            <input
              type="text"
              placeholder="Chain ID (BSC_BNB…)"
              value={chainId}
              onChange={(e) => setChainId(e.target.value)}
              className="deposits-search-input"
              style={{ maxWidth: "none", flex: "1 1 140px" }}
            />
            <input
              type="text"
              placeholder="Tx hash"
              value={txHash}
              onChange={(e) => setTxHash(e.target.value)}
              className="deposits-search-input"
              style={{ maxWidth: "none", flex: "1 1 200px" }}
            />
            <label className="deposits-page-limit">
              <span>Rows per page</span>
              <select
                value={pageLimit}
                onChange={handlePageLimitChange}
                className="deposits-search-input deposits-page-limit__select"
                disabled={loading}
              >
                {PAGE_LIMIT_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {error && (
            <p className="deposits-empty" style={{ color: "#b91c1c" }} role="alert">
              {error}
            </p>
          )}

          <div className="deposits-table-wrap">
            <table className="deposits-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>User</th>
                  <th>UUID</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Asset</th>
                  <th>Chain</th>
                  <th>Amount</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Tx Hash</th>
                  <th>Confirmations</th>
                  <th>Status</th>
                  <th>Credited</th>
                  <th>View</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={tableColSpan} className="deposits-empty">
                      <i className="fas fa-spinner fa-spin" /> Loading deposit history…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="deposits-empty">
                      No deposits found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const user = row.userId;
                    const uid = user?._id;
                    return (
                      <tr key={row._id}>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{userFullName(user)}</td>
                        <td>
                          {uid ? (
                            <Link
                              to={`/dashboard/trader/${uid}`}
                              className="deposits-mono"
                              style={{ textDecoration: "none" }}
                            >
                              {user?.uuid ?? "—"}
                            </Link>
                          ) : (
                            user?.uuid ?? "—"
                          )}
                        </td>
                        <td>{user?.emailId ?? "—"}</td>
                        <td>{user?.mobileNumber ?? "—"}</td>
                        <td>
                          <strong>{row.assetId ?? "—"}</strong>
                        </td>
                        <td>{row.chainId ?? "—"}</td>
                        <td>{formatNum(row.amount)}</td>
                        <td>
                          <CopyHashCell value={row.fromAddress} onCopy={handleCopy} />
                        </td>
                        <td>
                          <CopyHashCell value={row.toAddress} onCopy={handleCopy} />
                        </td>
                        <td>
                          <CopyHashCell
                            value={row.txHash}
                            head={10}
                            tail={8}
                            onCopy={handleCopy}
                          />
                        </td>
                        <td>
                          {row.confirmations != null
                            ? `${row.confirmations}/${row.confirmationThreshold ?? "—"}`
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`deposits-status deposits-status--${statusClass(row.status)}`}
                          >
                            {row.status ?? "—"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`deposits-status deposits-status--${
                              row.credited ? "success" : "pending"
                            }`}
                          >
                            {row.credited ? "Yes" : "No"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="withdrawal-btn withdrawal-btn--view"
                            onClick={() => setViewDetail(row)}
                            title="View details"
                          >
                            <i className="fas fa-eye" /> View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalCount > 0 && (
            <div className="withdrawal-pagination" style={{ padding: "0.75rem 1.5rem" }}>
              <button
                type="button"
                className="withdrawal-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>
              <span className="withdrawal-page-info">
                Showing {pageStart}–{pageEnd} of {totalCount}
                {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ""}
              </span>
              <button
                type="button"
                className="withdrawal-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}
        </section>
      </div>

      {viewDetail && (
        <div className="withdrawal-modal-overlay" onClick={() => setViewDetail(null)}>
          <div className="withdrawal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="withdrawal-modal__header">
              <h3>Deposit Details</h3>
              <button
                type="button"
                className="withdrawal-modal__close"
                onClick={() => setViewDetail(null)}
                aria-label="Close"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="withdrawal-modal__body">
              <dl className="withdrawal-dl">
                <div>
                  <dt>Record ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail._id || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Transaction ID</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.transactionId || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Created</dt>
                  <dd>{formatDate(viewDetail.createdAt)}</dd>
                </div>
                <div>
                  <dt>Credited At</dt>
                  <dd>{formatDate(viewDetail.creditedAt)}</dd>
                </div>
                <div>
                  <dt>User</dt>
                  <dd>{userFullName(viewDetail.userId)}</dd>
                </div>
                <div>
                  <dt>UUID</dt>
                  <dd className="withdrawal-mono">{viewDetail.userId?.uuid ?? "—"}</dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>{viewDetail.userId?.emailId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Mobile</dt>
                  <dd>{viewDetail.userId?.mobileNumber ?? "—"}</dd>
                </div>
                <div>
                  <dt>Asset</dt>
                  <dd>{viewDetail.assetId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Token ID</dt>
                  <dd>{viewDetail.tokenId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Chain</dt>
                  <dd>{viewDetail.chainId ?? "—"}</dd>
                </div>
                <div>
                  <dt>Amount</dt>
                  <dd>{formatNum(viewDetail.amount)}</dd>
                </div>
                <div>
                  <dt>From Address</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.fromAddress || "—"}
                  </dd>
                </div>
                <div>
                  <dt>To Address</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.toAddress || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Tx Hash</dt>
                  <dd className="withdrawal-mono withdrawal-mono--wrap">
                    {viewDetail.txHash || "—"}
                  </dd>
                </div>
                <div>
                  <dt>Confirmations</dt>
                  <dd>
                    {viewDetail.confirmations != null
                      ? `${viewDetail.confirmations} / ${viewDetail.confirmationThreshold ?? "—"}`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    <span
                      className={`deposits-status deposits-status--${statusClass(viewDetail.status)}`}
                    >
                      {viewDetail.status ?? "—"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Credited</dt>
                  <dd>{viewDetail.credited ? "Yes" : "No"}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default FundsDepositPage;
